// The Panel contract: the container's core abstraction. A PanelDescriptor
// wraps one *existing* mode's Route component for registration with dockview
// - it never decomposes a mode's own content. See panel-system/README below
// each file's header comment for how the pieces fit together.
import type { ReactNode } from 'react';

/** A project's authored dimension. 'shared' panels are valid for both. */
export type Dimension = '2D' | '3D';
export type PanelDimension = Dimension | 'shared';

/**
 * One of the editor's five named, Blender-style workspace presets (scene,
 * art/assets, systems, setup, code - the top tab strip in
 * panel-system/Workspace.tsx's PresetSwitcher switches between these).
 * `null` means the panel isn't reachable from any preset's tab strip
 * (currently only Preview, which bypasses the docked workspace entirely -
 * see EditorHost/ScreenEditor).
 */
export type PresetId = 'scene' | 'art' | 'systems' | 'setup' | 'code';

/** Stable panel id. Equal to the mode's URL segment (`/app/editor/:id/:mode`)
 * for every rail-navigable mode, so a PanelId doubles as a legacy route key. */
export type PanelId = string;

/**
 * The Panel contract every mode's `*Route` component satisfies to register
 * with the dock. `render` is intentionally a closure, not serializable
 * `params`: a mode's live props (e.g. Edit's `onAddAsset`) are host-provided
 * callbacks, not persisted state. dockview's own `params` only ever carries
 * `{ panelId }` (see PanelHost) - just enough to look this descriptor back
 * up from the registry when dockview reparents/restores the panel.
 */
export interface PanelDescriptor {
  readonly id: PanelId;
  /** Tab label. */
  readonly title: string;
  /** Which preset's tab strip this panel belongs to; `null` = not dockable
   * (handled outside the workspace - see Preview). */
  readonly presetId: PresetId | null;
  readonly dimension: PanelDimension;
  /** Fixed dock zone within the preset (see PanelZone); omitted means
   * 'center'. */
  readonly zone?: PanelZone;
  /**
   * Named subsystem this panel belongs to within its zone (e.g. 'logic',
   * 'audio', 'hud') - panels sharing a zone AND a group become tab siblings
   * in one dockview group, exactly as before this field existed; panels
   * sharing a zone but carrying *different* groups (or no group at all)
   * become separate, stacked dockview groups within that same zone instead
   * of one flat, unlabelled tab row. Exists because a preset like Systems
   * puts several unrelated subsystems' panels in the same zone (Logic/HUD/
   * Audio/FX/Data all use `zone: 'left'`) - without this, that zone was one
   * long tab strip with nothing but a panel's title prefix telling them
   * apart. Omitted means "its own group, alone in its zone unless another
   * panel explicitly shares this same omitted-group zone" - see
   * panelIdsByZoneForPreset for the exact bucketing rule.
   */
  readonly group?: string;
  /**
   * True for a mode whose Route owns a live WebGL `<Canvas>` (the roadmap's
   * own audit: Edit, Map Builder, Characters, Animation, FX, Cutscenes).
   * These get the stable-size wrapper (see useDevicePixelSize) so a
   * dockview tab-hide never hands their canvas a 0x0 resize.
   */
  readonly canvasOwning?: boolean;
  /** Renders the mode's own, unmodified Route. */
  readonly render: () => ReactNode;
  /**
   * Optional per-panel extra state to fold into the persisted layout's
   * `panels[id].params`, beyond what dockview already tracks (group/tab
   * position, active tab). No current descriptor needs this; it exists so a
   * future panel can persist something of its own without a contract change.
   */
  readonly serializeParams?: () => Record<string, unknown>;
  readonly deserializeParams?: (raw: Record<string, unknown>) => void;
  /** Extra cleanup beyond React unmount, run when dockview removes this
   * panel from the layout (e.g. a preset switch evicts it). */
  readonly dispose?: () => void;
}

/**
 * Which fixed dock zone within a preset this panel belongs to (roadmap.md
 * section 3.2's decision: "build fixed dock zones (left/right/bottom) with
 * tabbed panel groups... not free-form docking"). Panels sharing a zone
 * within one preset become tab siblings in one group; different zones
 * become separate, spatially adjacent groups (left | center | right,
 * left-to-right). Defaults to 'center' when omitted - most single-panel
 * modes (Edit, Cutscenes, Intro Sequences, the FX particles legacy panel,
 * every Game Setup panel) only ever have one panel in their preset, so the
 * zone is moot for them.
 */
export type PanelZone = 'left' | 'center' | 'right';

export interface PresetDescriptor {
  readonly id: PresetId;
  readonly label: string;
}

export function isDimensionValid(dimension: PanelDimension, projection: Dimension): boolean {
  return dimension === 'shared' || dimension === projection;
}
